import type { FolderContentHistoryResponseDatagramBuf } from "~/classes/generated/response"

import { DatagramBuf } from "~/classes/generated/datagram"
import { FolderContentHistoryRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseRequestId, parseVector } from "~/utils/flatbuffers/datagram"
import { addEntityPropertyTimestampToBuf } from "~/utils/flatbuffers/entity"
import { addFileToBuf, parseFileProperty } from "~/utils/flatbuffers/file"
import { parseFileId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const _console = useLogger("file/history")

export function sendFolderContentHistoryRequests(requests: FolderContentHistoryRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()
  const fbb = new DatagramBufferBuilder(64)
  const requestVector = []
  for (const request of requests) {
    const folder = addFileToBuf(fbb, request.folder)
    let gt, lt
    if (request.gt)
      gt = addEntityPropertyTimestampToBuf(fbb, request.gt)
    if (request.lt)
      lt = addEntityPropertyTimestampToBuf(fbb, request.lt)
    const requestId = addRequestIdToBuf(fbb, request.requestId)
    FolderContentHistoryRequestDatagramBuf.startFolderContentHistoryRequestDatagramBuf(fbb)
    FolderContentHistoryRequestDatagramBuf.addRequestId(fbb, requestId)
    FolderContentHistoryRequestDatagramBuf.addFolder(fbb, folder)
    FolderContentHistoryRequestDatagramBuf.addOffline(fbb, request.offline)
    FolderContentHistoryRequestDatagramBuf.addPageSize(fbb, request.pageSize)
    FolderContentHistoryRequestDatagramBuf.addLimit(fbb, request.limit)

    if (isDefined(gt))
      FolderContentHistoryRequestDatagramBuf.addGt(fbb, gt)
    if (isDefined(lt))
      FolderContentHistoryRequestDatagramBuf.addLt(fbb, lt)

    const historyRequest = FolderContentHistoryRequestDatagramBuf.endFolderContentHistoryRequestDatagramBuf(fbb)
    RequestDatagramBuf.startRequestDatagramBuf(fbb)
    RequestDatagramBuf.addFolderContentHistoryRequest(fbb, historyRequest)
    requestVector.push(RequestDatagramBuf.endRequestDatagramBuf(fbb))
  }

  const vector = DatagramBuf.createRequestVector(fbb, requestVector)
  DatagramBuf.startDatagramBuf(fbb)
  DatagramBuf.addRequest(fbb, vector)
  fbb.finish(DatagramBuf.endDatagramBuf(fbb))
  sendWorkerDatagramBuf(fbb.asUint8Array())
}

/** Parse a Flatbuffers representation of a response */
export function handleFolderContentHistoryResponseDatagramBuf(datagram: FolderContentHistoryResponseDatagramBuf) {
  onFolderContentHistoryResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    properties: parseVector(i => datagram.properties(i)!, datagram.propertiesLength()).map(parseFileProperty),
    offline: datagram.offline(),
    folderId: parseFileId(datagram.folderId()!),
  })
}

export function onFolderContentHistoryResponse(response: FolderContentHistoryResponseDatagram) {
  const { emitInterfaceEvent } = useEvents()
  const { emitEntityEvent } = useEvents()

  if (response.error)
    return onFolderContentHistoryErrored(response.error)
  if (!response.properties)
    throw new Error("No properties in history response")

  const folderId = response.folderId
  const properties = response.properties

  emitEntityEvent("folderHistory", folderId, { properties }, response.offline)
  emitInterfaceEvent("folderContentHistoryResponse", response, response.requestId)
}

export function onFolderContentHistoryErrored(error: DatagramError) {
  const { notifyDatagramError } = useAlerts()

  return notifyDatagramError(error, "File deletion was unsuccessful")
}
